import React from "react";
import { useHistory } from "react-router-dom";
import { TooltipComponent } from "../../Component";
import { getPublicUrl } from "../../../utils/Utils";
import PartnerStatusBadge from "../medical-partner-admin/PartnerStatusBadge";

function listingBadge(decision) {
  const d = String(decision || "pending").toLowerCase();
  if (d === "approved") return { label: "Approved", className: "badge-dim bg-success" };
  if (d === "rejected") return { label: "Rejected", className: "badge-dim bg-danger" };
  return { label: "Pending", className: "badge-dim bg-warning" };
}

/** Per-clinic teleconsultation channel controls (admin). Props: clinicsSummary, busyClinicId, onClinicChannelPatch. */
const PartnerTeleconsultationClinicsChannelsTable = ({ clinicsSummary, busyClinicId, onClinicChannelPatch }) => {
  const history = useHistory();
  const list = Array.isArray(clinicsSummary) ? clinicsSummary : [];

  if (list.length === 0) return null;

  return (
    <div className="card card-bordered">
      <div className="card-inner p-0">
        <div className="p-3 border-bottom d-flex flex-wrap align-items-center justify-content-between gap-2">
          <h6 className="overline-title mb-0">Clinics — channels</h6>
          <span className="text-soft small mb-0">
            Toggle offers TC and MT here. Catalog listing is set in Practitioner approvals (read-only below).
          </span>
        </div>
        <div className="alert alert-light border small mb-0 mx-3 mt-3" role="status">
          The former <strong>Platform TC</strong> switch has been retired. Approve or reject public catalog listing in{" "}
          <button
            type="button"
            className="btn btn-link btn-sm p-0 align-baseline"
            onClick={() => history.push(getPublicUrl("teleconsultations/practitioner-approvals"))}
          >
            Practitioner approvals
          </button>
          .
        </div>
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th scope="col">Clinic</th>
                <th scope="col">Offers TC</th>
                <th scope="col">Offers MT</th>
                <th scope="col">Catalog listing</th>
                <th scope="col">Listing active</th>
                <th scope="col">Clinic status</th>
                <th scope="col" className="text-end">
                  Shortcuts
                </th>
              </tr>
            </thead>
            <tbody>
              {list.map((c, idx) => {
                const cid = c?._id ? String(c._id) : "";
                const rowKey = cid || `clinic-channel-${idx}`;
                const rowBusy = Boolean(busyClinicId && cid && String(busyClinicId) === cid);
                const offersTc = !!c.offersTeleconsultation;
                const offersMt = c.offersMedicalTourism !== false;
                const badge = listingBadge(c.platformListingDecision);
                return (
                  <tr key={rowKey}>
                    <td className="align-middle">{c.name || "—"}</td>
                    <td className="align-middle">
                      <div className="custom-control custom-switch">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id={`tc-offers-${rowKey}`}
                          checked={offersTc}
                          disabled={!cid || rowBusy}
                          onChange={(e) => {
                            if (!cid) return;
                            void onClinicChannelPatch(cid, { offersTeleconsultation: e.target.checked });
                          }}
                        />
                        <label className="custom-control-label sr-only" htmlFor={`tc-offers-${rowKey}`}>
                          Offers teleconsultation
                        </label>
                      </div>
                    </td>
                    <td className="align-middle">
                      <div className="custom-control custom-switch">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id={`mt-offers-${rowKey}`}
                          checked={offersMt}
                          disabled={!cid || rowBusy}
                          onChange={(e) => {
                            if (!cid) return;
                            void onClinicChannelPatch(cid, { offersMedicalTourism: e.target.checked });
                          }}
                        />
                        <label className="custom-control-label sr-only" htmlFor={`mt-offers-${rowKey}`}>
                          Offers medical tourism
                        </label>
                      </div>
                    </td>
                    <td className="align-middle">
                      <div className="d-flex flex-column align-items-start" style={{ minWidth: 96 }}>
                        <span className={`badge badge-pill ${badge.className}`}>{badge.label}</span>
                        <span className="text-soft small mt-1">Set in Practitioner approvals</span>
                      </div>
                    </td>
                    <td className="align-middle text-soft small">
                      {c.status === "active" || c.status === "Active" ? "Yes" : "No"}
                    </td>
                    <td className="align-middle">
                      <PartnerStatusBadge status={c.status} labelMode="listing" />
                    </td>
                    <td className="align-middle text-end">
                      <ul className="nk-tb-actions gx-1 justify-content-end">
                        <li>
                          <TooltipComponent
                            id={`tc-prices-${rowKey}`}
                            tag="a"
                            href="#prices"
                            containerClassName="btn btn-trigger btn-icon"
                            icon="calc"
                            direction="top"
                            text="Clinic prices & catalog"
                            onClick={(e) => {
                              e.preventDefault();
                              if (cid) history.push(getPublicUrl(`clinics/${cid}/prices`));
                            }}
                          />
                        </li>
                        <li>
                          <TooltipComponent
                            id={`tc-cal-${rowKey}`}
                            tag="a"
                            href="#calendar"
                            containerClassName="btn btn-trigger btn-icon"
                            icon="calendar"
                            direction="top"
                            text="Slots / calendar"
                            onClick={(e) => {
                              e.preventDefault();
                              if (cid) history.push(getPublicUrl(`clinics/${cid}/calendar`));
                            }}
                          />
                        </li>
                        <li>
                          <TooltipComponent
                            id={`tc-pract-${rowKey}`}
                            tag="a"
                            href="#practitioners"
                            containerClassName="btn btn-trigger btn-icon"
                            icon="users"
                            direction="top"
                            text="Practitioners"
                            onClick={(e) => {
                              e.preventDefault();
                              if (cid) history.push(getPublicUrl(`clinics/${cid}/practitioners`));
                            }}
                          />
                        </li>
                      </ul>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PartnerTeleconsultationClinicsChannelsTable;
